import * as THREE from 'three';
import type { RideId } from '../content/rides';
import { clamp, fbm, lerp, smoothstep } from '../core/math';
import { Random } from '../core/Random';
import { batchStatic } from './batch';
import { animateDog, buildDog, type Dog } from './builders/animals';
import {
  doubleWide,
  gasCanopy,
  gasStation,
  junkyardShack,
  laundromat,
  parkOffice,
  pawnShop,
  priceSign,
  tubeMan,
} from './builders/buildings';
import { flamingo, gnome } from './builders/decor';
import {
  basketballHoop,
  bench,
  bike,
  bmxRamp,
  bush,
  chainLinkFence,
  cooler,
  couch,
  crushedCar,
  doghouse,
  dumpster,
  firePit,
  flagpole,
  fridge,
  gasPump,
  lawnChair,
  leafyTree,
  mailbox,
  mattress,
  oilDrumSmoker,
  payphone,
  pennants,
  picketFence,
  pineTree,
  powerPole,
  rockingChair,
  signBoard,
  streetLight,
  tireStack,
  trashBags,
  trashCan,
  washer,
  waveFlag,
  wire,
} from './builders/props';
import { buildTrailer, type TrailerSpec } from './builders/trailer';
import { buildRide } from './builders/rides';
import { buildFairgrounds, type Fairgrounds } from './fairgrounds';
import { buildCarLot, buildWestStrip } from './strip';
import { buildBoatOnTrailer, buildCar, buildTruckNutz, type BuiltCar } from './builders/vehicles';
import { CollisionWorld } from './Collision';
import {
  buildBackForty,
  buildCommons,
  buildPossumTrot,
  buildVacantHome,
  type BackFortyProps,
  type VacantLot,
} from './districts';
import { HOME_LOTS, homeSpots, LOT_7, POSSUM_TROT_X, type HomeFrame, type HomeKind } from './home';
import { HomeLot, type TrailerStyle } from './HomeLot';
import { Lot10Site } from './lot10';
import {
  BACK_FORTY,
  BACK_FORTY_ROAD,
  backFortyDepth,
  COMMONS,
  CRICK,
  CAR_LOT,
  crickZ,
  DECK_DEPTH,
  FAIRGROUNDS_ROAD,
  GUN_RANGE,
  HIGHWAY,
  inRect,
  LOTS,
  MUD_BOG,
  mudAt,
  NORTH_ROAD,
  POSSUM_TROT,
  rect,
  RODEO,
  SPOTS,
  STEPS_DEPTH,
  WEST_LOT,
  TRAILER_LENGTH,
  TRAILER_WIDTH,
  type LotId,
  type Rect,
} from './layout';
import { box, cyl, M, mat, mesh, texMat } from './materials';
import { ParticleSystem } from './Particles';
import {
  asphaltTexture,
  checkeredFlagTexture,
  americanFlagTexture,
  grassTexture,
  gravelTexture,
  highwayTexture,
  signTexture,
} from './textures';

type Animator = (time: number, dt: number, night: number) => void;

const TRAILER_SPECS: Record<
  Exclude<LotId, 'vacant' | 'player'>,
  Omit<TrailerSpec, 'doorX' | 'hitch' | 'steps'>
> = {
  tammy: {
    color: '#f6d3e2',
    stripe: '#c2185b',
    windows: [
      { x: -3.6, side: 1, kind: 'blinds' },
      { x: 0.2, side: 1, kind: 'glass' },
      { x: -2, side: -1, kind: 'dim' },
      { x: 2.6, side: -1, kind: 'glass' },
    ],
    skirting: 'lattice',
    awning: true,
    porchLight: true,
    seed: 5,
  },
  earl: {
    color: '#ebe6d6',
    stripe: '#3a6b3a',
    windows: [
      { x: -3.8, side: 1, kind: 'foil' },
      { x: 1.6, side: 1, kind: 'glass' },
      { x: 0, side: -1, kind: 'foil' },
    ],
    ac: -1.2,
    skirting: 'metal',
    porchLight: true,
    seed: 6,
  },
  dale: {
    color: '#d6c39a',
    stripe: '#7a4a2a',
    rusty: true,
    tarp: true,
    tiresOnRoof: true,
    windows: [
      { x: -3.5, side: 1, kind: 'cardboard' },
      { x: 1.5, side: 1, kind: 'foil' },
      { x: 0, side: -1, kind: 'cardboard' },
    ],
    skirting: 'none',
    seed: 8,
  },
  jenkins: {
    color: '#efe9d4',
    stripe: '#6b4a2a',
    windows: [
      { x: -3.5, side: 1, kind: 'blinds' },
      { x: 1.6, side: 1, kind: 'blinds' },
      { x: 0, side: -1, kind: 'dim' },
    ],
    skirting: 'metal',
    awning: true,
    porchLight: true,
    seed: 9,
  },
  skeeter: {
    color: '#f2e49c',
    stripe: '#b07a2b',
    windows: [
      { x: -3.5, side: 1, kind: 'blinds' },
      { x: 1.2, side: 1, kind: 'glass' },
      { x: -1, side: -1, kind: 'flag' },
    ],
    ac: -1.2,
    skirting: 'lattice',
    seed: 11,
  },
  brenda: {
    color: '#c9dcf0',
    stripe: '#1f4e8c',
    windows: [
      { x: -3.5, side: 1, kind: 'glass' },
      { x: 1.5, side: 1, kind: 'blinds' },
      { x: 0, side: -1, kind: 'dim' },
    ],
    skirting: 'lattice',
    porchLight: true,
    seed: 12,
  },
  wanda: {
    color: '#d6e8cf',
    stripe: '#4f7a3a',
    windows: [
      { x: -3.5, side: 1, kind: 'blinds' },
      { x: 1.5, side: 1, kind: 'glass' },
    ],
    skirting: 'lattice',
    awning: true,
    seed: 14,
  },
  hank: {
    color: '#efe0cb',
    stripe: '#7a3b2e',
    windows: [
      { x: -3.4, side: 1, kind: 'foil' },
      { x: 1.4, side: 1, kind: 'glass' },
      { x: 0, side: -1, kind: 'dim' },
    ],
    ac: 3.6,
    skirting: 'metal',
    seed: 15,
  },
};

const JUNKYARD = rect(38, -62, 72, -34);

/** The 3D environment of Lucky Acres: static scenery, colliders and ambient animation. */
export class World {
  readonly root = new THREE.Group();
  readonly dynamic = new THREE.Group();
  readonly collision = new CollisionWorld();
  /** Boxes the camera shouldn't pass through (big structures). */
  readonly occluders: THREE.Box3[] = [];
  readonly sparks = new ParticleSystem(2200, true);
  readonly smoke = new ParticleSystem(700, false);
  readonly streetLights: THREE.PointLight[] = [];
  readonly porchLight = new THREE.PointLight('#ffcf80', 0, 9, 1.6);
  readonly canopyLight = new THREE.PointLight('#fff2d0', 0, 22, 1.4);
  readonly fireBarrelLight = new THREE.PointLight('#ff8a3a', 0, 8, 1.6);
  readonly campLight = new THREE.PointLight('#ff7a2a', 0, 16, 1.4);

  /** Player homes on the map, by HOME_LOTS index (yours and your neighbors'). */
  readonly homes = new Map<number, HomeLot>();
  private readonly vacant: Array<VacantLot & { ids: number[] }> = [];

  /** Your Camaro (whichever lot you live on). */
  /** Whatever's parked in your driveway (and what you drive). */
  ride!: BuiltCar;
  readonly rideRoot = new THREE.Group();
  private rideKey = '';
  private truckNutz: THREE.Group | null = null;
  private parkSignClassic!: THREE.Mesh;
  private parkSignApex!: THREE.Mesh;
  private parkSignOwned!: THREE.Mesh;
  private tank!: Dog;
  private readonly animators: Animator[] = [];
  /** The rodeo bull and friends, for the gameplay code that animates them. */
  fair!: Fairgrounds;
  /** Extra light from a bonfire near the BMX field's barrel (the couch, when it goes up). */
  fireBoost = 0;
  /** The couch on the curb by the BMX field (it burns well). */
  curbCouch!: THREE.Group;
  /** Moving parts of the Back Forty (the still's fire). */
  backForty!: BackFortyProps;
  /** Lot 10: burnt out, until somebody fixes it up and rents it. */
  lot10!: Lot10Site;
  private readonly smokeSources: THREE.Vector3[] = [];
  private smokeTimer = 0;

  constructor(private readonly scene: THREE.Scene) {
    this.root.name = 'static-world';
    this.dynamic.name = 'dynamic-world';
    scene.add(this.root, this.dynamic, this.sparks.points, this.smoke.points);
    scene.add(this.porchLight, this.canopyLight, this.fireBarrelLight, this.campLight);
    this.campLight.position.set(SPOTS.barrelCamp.x + 0.2, 1.8, SPOTS.barrelCamp.z + 1.2);
  }

  build(): { before: number; after: number } {
    const rng = new Random(1974);
    this.buildGround();
    this.buildRoads();
    this.buildLots(rng);
    this.buildPlayerCar();
    this.buildEntrance(rng);
    this.buildStores(rng);
    this.buildJunkyard(rng);
    this.buildUtilities();
    buildPossumTrot(this, rng);
    buildCommons(this, rng);
    this.backForty = buildBackForty(this, rng);
    buildWestStrip(this, rng);
    const lot = buildCarLot(this, rng);
    this.animators.push((t) => waveTubeMan(lot.tube, t));
    this.fair = buildFairgrounds(this, rng);
    this.buildNature(rng);
    const stats = batchStatic(this.root);
    this.buildVacantHomes();
    return stats;
  }

  // ---------------------------------------------------------------------------
  // Homes
  // ---------------------------------------------------------------------------

  /** Builds a player's home on a lot (replacing whatever was there). */
  placeHome(frame: HomeFrame, style: TrailerStyle, label: string, kind: HomeKind = 'single'): HomeLot {
    this.removeHome(frame.index);
    this.setVacant(frame.index, false);
    const home = new HomeLot(this, this.dynamic, frame, style, label, kind);
    this.homes.set(frame.index, home);
    return home;
  }

  removeHome(index: number): void {
    const home = this.homes.get(index);
    if (!home) return;
    home.dispose();
    this.homes.delete(index);
    this.setVacant(index, true);
  }

  /** Hang the (one) porch light at your home. */
  setPorchLight(home: HomeLot): void {
    home.porchLight(this.porchLight.position);
  }

  private buildVacantHomes(): void {
    HOME_LOTS.forEach((_, i) => {
      const v = buildVacantHome(i);
      v.group.visible = false;
      batchStatic(v.group);
      this.scene.add(v.group);
      this.vacant.push({ ...v, ids: [] });
      this.setVacant(i, true);
    });
  }

  private setVacant(index: number, on: boolean): void {
    const v = this.vacant[index];
    if (!v || v.group.visible === on) return;
    v.group.visible = on;
    for (const id of v.ids) this.collision.remove(id);
    v.ids = on ? v.shapes.map((c) => this.collision.addCircle(c.x, c.z, c.r)) : [];
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  place<T extends THREE.Object3D>(
    obj: T,
    x: number,
    z: number,
    rotY = 0,
    parent: THREE.Object3D = this.root,
  ): T {
    obj.position.x = x;
    obj.position.z = z;
    obj.rotation.y = rotY;
    parent.add(obj);
    return obj;
  }

  animate(fn: Animator): void {
    this.animators.push(fn);
  }

  addSmoke(x: number, y: number, z: number): void {
    this.smokeSources.unshift(new THREE.Vector3(x, y, z));
  }

  /** A solid structure: blocks movement and the camera. */
  solid(cx: number, cz: number, w: number, d: number, h: number, quarterTurns = 0): void {
    this.collision.addRect(cx, cz, w, d, quarterTurns);
    const swap = Math.abs(quarterTurns) % 2 === 1;
    const hw = (swap ? d : w) / 2;
    const hd = (swap ? w : d) / 2;
    this.occluders.push(
      new THREE.Box3(new THREE.Vector3(cx - hw, 0, cz - hd), new THREE.Vector3(cx + hw, h, cz + hd)),
    );
  }

  private groundPlane(
    r: Rect,
    material: THREE.Material,
    tileU: number,
    tileV: number,
    y: number,
  ): THREE.Mesh {
    const w = r.maxX - r.minX;
    const d = r.maxZ - r.minZ;
    const geo = new THREE.PlaneGeometry(w, d);
    const uv = geo.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) * w) / tileU, (uv.getY(i) * d) / tileV);
    geo.rotateX(-Math.PI / 2);
    const m = mesh(geo, material, (r.minX + r.maxX) / 2, y, (r.minZ + r.maxZ) / 2, {
      cast: false,
      receive: true,
    });
    this.root.add(m);
    return m;
  }

  private surface(tex: THREE.Texture, color = '#ffffff', offset = -1): THREE.MeshStandardMaterial {
    const m = texMat(tex, { color, rough: 1 }).clone();
    m.polygonOffset = true;
    m.polygonOffsetFactor = offset;
    m.polygonOffsetUnits = offset;
    return m;
  }

  // ---------------------------------------------------------------------------
  // Ground & roads
  // ---------------------------------------------------------------------------

  private buildGround(): void {
    const size = 300;
    const seg = 150;
    const geo = new THREE.PlaneGeometry(size, size, seg, seg);
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, 0, 14);
    const uv = geo.attributes.uv as THREE.BufferAttribute;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * (size / 4), uv.getY(i) * (size / 4));
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const colors = new Float32Array(pos.count * 3);
    const grassA = new THREE.Color('#4c7a30');
    const grassB = new THREE.Color('#7d9a3c');
    const dry = new THREE.Color('#a8a25a');
    const dirt = new THREE.Color('#8b7152');
    const mud = new THREE.Color('#3f3527');
    const dead = new THREE.Color('#6b6a45');
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      c.copy(grassA).lerp(grassB, fbm(x * 0.03, z * 0.03, 3, 2));
      c.lerp(dry, smoothstep(0.55, 0.75, fbm(x * 0.02 + 40, z * 0.02 - 13, 3, 3)) * 0.7);
      c.lerp(dirt, this.dirtiness(x, z));
      // The Back Forty: dead grass and mud.
      const back = backFortyDepth(x, z);
      if (back > 0) {
        c.lerp(dead, back * 0.75);
        c.lerp(mud, back * smoothstep(0.35, 0.7, fbm(x * 0.06 - 7, z * 0.06 + 3, 3, 11)) * 0.85);
        const crick = Math.abs(z - crickZ(x));
        if (crick < CRICK.width / 2 + 3) c.lerp(mud, 0.8 * (1 - crick / (CRICK.width / 2 + 3)));
      }
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const ground = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ map: grassTexture(), vertexColors: true, roughness: 1 }),
    );
    ground.receiveShadow = true;
    ground.userData.noBatch = true;
    this.scene.add(ground);
  }

  private dirtiness(x: number, z: number): number {
    let d = smoothstep(0.6, 0.74, fbm(x * 0.05 + 3.1, z * 0.05 - 1.7, 3, 5)) * 0.8;
    if (Math.abs(x) > 4 && Math.abs(x) < 28 && z > -42 && z < 38)
      d = Math.max(d, 0.18 + 0.4 * fbm(x * 0.25, z * 0.25, 2, 9));
    if (Math.abs(x) < 6 && z > -54 && z < 58) d = Math.max(d, 0.55);
    if (Math.abs(x - POSSUM_TROT_X) < 6 && z > -108 && z < 42) d = Math.max(d, 0.55);
    if (inRect(COMMONS, x, z, 1)) d = Math.max(d, 0.25 + 0.35 * fbm(x * 0.2, z * 0.2, 2, 4));
    if (inRect(JUNKYARD, x, z, 1)) d = 0.92;
    if (inRect(rect(-48, 66, 40, 84), x, z, 2)) d = Math.max(d, 0.5);
    // The rodeo ring is churned-up dirt; the mud bog and gun range are bare.
    const ring = Math.hypot((x - RODEO.x) / RODEO.rx, (z - RODEO.z) / RODEO.rz);
    if (ring < 1.15) d = Math.max(d, ring < 1 ? 0.95 : 0.95 * (1 - (ring - 1) / 0.15));
    if (inRect(MUD_BOG, x, z, 4)) d = Math.max(d, 0.9);
    if (inRect(GUN_RANGE, x, z, 2)) d = Math.max(d, 0.6 + 0.3 * fbm(x * 0.2, z * 0.2, 2, 6));
    if (inRect(WEST_LOT, x, z, 3) || inRect(CAR_LOT, x, z, 3)) d = Math.max(d, 0.5);
    const bmx = Math.hypot(x - SPOTS.bmxField.x, z - SPOTS.bmxField.z);
    if (bmx < 12) d = Math.max(d, 0.85 * (1 - bmx / 12) + 0.2);
    for (const lot of Object.values(LOTS)) {
      const dd = Math.hypot(x - lot.door.x, z - (lot.door.z + 1.5));
      if (dd < 3) d = Math.max(d, 0.75 * (1 - dd / 3));
    }
    for (const lot of HOME_LOTS) {
      const door = homeSpots(lot).trailerDoor;
      const dd = Math.hypot(x - door.x, z - (door.z + 1.5));
      if (dd < 3) d = Math.max(d, 0.75 * (1 - dd / 3));
    }
    return clamp(d, 0, 1);
  }

  private buildRoads(): void {
    const gravel = this.surface(gravelTexture());
    const darkGravel = this.surface(gravelTexture(), '#9a8c78', -2);
    const asphalt = this.surface(asphaltTexture());
    this.groundPlane(rect(-3.5, -44.5, 3.5, 56), gravel, 4, 4, 0.02);
    this.groundPlane(NORTH_ROAD, gravel, 4, 4, 0.02);
    this.groundPlane(
      rect(POSSUM_TROT.minX, POSSUM_TROT.minZ + 7, POSSUM_TROT.maxX, POSSUM_TROT.maxZ),
      gravel,
      4,
      4,
      0.02,
    );
    this.groundPlane(BACK_FORTY_ROAD, this.surface(gravelTexture(), '#6e604c', -2), 4, 4, 0.021);
    this.groundPlane(
      rect(HIGHWAY.minX, HIGHWAY.minZ, HIGHWAY.maxX, HIGHWAY.maxZ),
      this.surface(highwayTexture()),
      16,
      10,
      0.03,
    );
    this.groundPlane(rect(-46, 67, 38, 81), asphalt, 6, 6, 0.025);
    this.groundPlane(rect(-3.5, 66, 3.5, 67), asphalt, 6, 6, 0.025);
    this.groundPlane(JUNKYARD, darkGravel, 4, 4, 0.02);
    // The south strip's gravel lot, Cooter's, and Fairgrounds Road.
    this.groundPlane(WEST_LOT, gravel, 4, 4, 0.02);
    this.groundPlane(rect(-108, 79, -47, 81), gravel, 4, 4, 0.021);
    this.groundPlane(CAR_LOT, asphalt, 6, 6, 0.025);
    this.groundPlane(rect(38, 67, 42, 81), asphalt, 6, 6, 0.025);
    this.groundPlane(FAIRGROUNDS_ROAD, gravel, 4, 4, 0.02);
    this.groundPlane(rect(54, -44, 104, -40), gravel, 4, 4, 0.021);
    this.groundPlane(rect(61, 14.5, RODEO.x - RODEO.rx + 0.5, 21.5), gravel, 4, 4, 0.021);
    this.root.add(box(80, 0.12, 1.4, mat('#b9b6ae'), -4, 0.06, 81.4, { cast: false }));
    for (let x = -42; x <= 34; x += 3.2)
      this.root.add(box(0.12, 0.01, 3.5, mat('#e8e6dc'), x, 0.035, 70.5, { cast: false }));
    for (const lot of Object.values(LOTS)) {
      if (lot.id === 'player') continue;
      const x0 = lot.side > 0 ? 3.5 : -11.5;
      this.groundPlane(rect(x0, lot.z + 0.8, x0 + 8, lot.z + 6.2), darkGravel, 4, 4, 0.021);
    }
    for (const lot of HOME_LOTS) {
      const x0 = lot.side > 0 ? lot.streetX + 3.5 : lot.streetX - 11.5;
      this.groundPlane(rect(x0, lot.rowZ + 0.8, x0 + 8, lot.rowZ + 6.2), darkGravel, 4, 4, 0.021);
    }
  }

  // ---------------------------------------------------------------------------
  // Lots
  // ---------------------------------------------------------------------------

  private buildLots(rng: Random): void {
    for (const id of Object.keys(LOTS) as LotId[]) {
      const lot = LOTS[id];
      if (id === 'vacant') {
        this.buildVacantLot();
        continue;
      }
      // Lot 7 is a player home (see HomeLot); it's built when someone lives there.
      if (id === 'player') continue;
      const spec = TRAILER_SPECS[id];
      const doorX = -lot.side * 4;
      const t = buildTrailer({ ...spec, doorX, hitch: -lot.side, steps: lot.deck ? 'deck' : 'blocks' });
      this.place(t, lot.trailer.x, lot.trailer.z);
      this.solid(lot.trailer.x, lot.trailer.z, TRAILER_LENGTH, TRAILER_WIDTH, 3.4);
      if (lot.deck) this.collision.addRect(lot.door.x, lot.door.z + DECK_DEPTH / 2, 3.5, DECK_DEPTH);
      else this.collision.addRect(lot.door.x, lot.door.z + STEPS_DEPTH / 2, 1.0, STEPS_DEPTH);
      // Hitch tongue at the street end.
      this.collision.addRect(lot.trailer.x - lot.side * 7.2, lot.trailer.z, 2.2, 1.2);

      const mb = mailbox(String(lot.number));
      this.place(mb, lot.side * 4.7, lot.z - 0.4, lot.side > 0 ? Math.PI : 0);
      this.collision.addCircle(lot.side * 4.7, lot.z - 0.4, 0.2);
      const can = trashCan(rng);
      this.place(can, lot.side * 9.4, lot.z - 1.8);
      this.collision.addCircle(lot.side * 9.4, lot.z - 1.8, 0.35);
      if (rng.chance(0.6)) this.place(trashBags(rng, 2), lot.side * 9.6, lot.z - 1.0);
      for (let i = 0; i < 4; i++) {
        if (rng.chance(0.6)) this.place(bush(rng), lot.trailer.x + rng.range(-5.5, 5.5), lot.z - 2.1);
      }
    }
    this.decorateEarl(rng);
    this.decorateTammy(rng);
    this.decorateDale(rng);
    this.decorateJenkins();
    this.decorateSkeeter();
    this.decorateBrenda();
    this.decorateWanda();
    this.decorateHank();
  }

  private buildVacantLot(): void {
    // The wreck, its sign and its weeds can all change (somebody might fix it up): see lot10.ts.
    this.lot10 = new Lot10Site(this.dynamic, this.collision, this.occluders);
    const lot = LOTS.vacant;
    const cart = new THREE.Group();
    cart.add(box(0.9, 0.6, 0.55, mat('#b8bcc0', { metal: 0.6 }), 0, 0.75, 0));
    cart.rotation.z = 1.4;
    this.place(cart, 22, lot.z + 3, 0.6);
  }

  private decorateEarl(rng: Random): void {
    const lot = LOTS.earl;
    const smoker = oilDrumSmoker();
    this.place(smoker, SPOTS.earlGrill.x, SPOTS.earlGrill.z - 1.0, 0.2);
    this.collision.addRect(SPOTS.earlGrill.x, SPOTS.earlGrill.z - 1.0, 1.3, 0.8);
    this.smokeSources.push(new THREE.Vector3(SPOTS.earlGrill.x - 0.35, 1.7, SPOTS.earlGrill.z - 1.0));
    const truck = buildCar({ kind: 'pickup', color: '#2e4f8a' });
    this.place(truck.group, 7.6, lot.z + 3.6, Math.PI);
    const nutz = buildTruckNutz();
    truck.rear.add(nutz);
    this.collision.addRect(7.6, lot.z + 3.6, 5.4, 2.1);
    const pit = firePit();
    this.place(pit, SPOTS.earlFirePit.x, SPOTS.earlFirePit.z);
    this.collision.addCircle(SPOTS.earlFirePit.x, SPOTS.earlFirePit.z, 0.75);
    const colors = ['#2f8f3a', '#c0392b', '#2b6cb0', '#e0b000'];
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + 0.4;
      const ch = lawnChair(colors[i] as string);
      this.place(
        ch,
        SPOTS.earlFirePit.x + Math.cos(a) * 1.7,
        SPOTS.earlFirePit.z + Math.sin(a) * 1.7,
        -a - Math.PI / 2,
      );
    }
    const dh = doghouse();
    this.place(dh, SPOTS.tankHouse.x + 1.2, SPOTS.tankHouse.z - 0.6, -Math.PI / 2);
    this.collision.addRect(SPOTS.tankHouse.x + 1.2, SPOTS.tankHouse.z - 0.6, 1.4, 1.3);
    this.tank = buildDog('pitbull');
    this.place(
      this.tank.group,
      SPOTS.tankHouse.x - 0.4,
      SPOTS.tankHouse.z - 0.2,
      -Math.PI / 2 - 0.4,
      this.dynamic,
    );
    this.collision.addCircle(SPOTS.tankHouse.x - 0.4, SPOTS.tankHouse.z - 0.2, 0.45);
    this.dynamic.add(
      wire(
        new THREE.Vector3(SPOTS.tankHouse.x + 0.6, 0.5, SPOTS.tankHouse.z - 0.6),
        new THREE.Vector3(SPOTS.tankHouse.x - 0.3, 0.45, SPOTS.tankHouse.z - 0.1),
        0.25,
      ),
    );
    const flag = flagpole(5, texMat(checkeredFlagTexture(), { side: THREE.DoubleSide }), 1.6, 1.0);
    this.place(flag.group, 25.5, lot.z + 6.5);
    this.animators.push((t) => waveFlag(flag.flag, t, 0.8));
    this.collision.addCircle(25.5, lot.z + 6.5, 0.15);
    this.place(cooler(), SPOTS.earlFirePit.x + 2.4, SPOTS.earlFirePit.z + 0.6);
    this.place(tireStack(rng, 3), 26.5, lot.z - 1.5);
    const tank = this.tank;
    this.animators.push((t) => animateDog(tank, t, 0, 0.4));
  }

  private decorateTammy(rng: Random): void {
    const lot = LOTS.tammy;
    const van = buildCar({ kind: 'van', color: '#9c2f4f' });
    this.place(van.group, -7.8, lot.z + 3.6, 0);
    this.collision.addRect(-7.8, lot.z + 3.6, 5.2, 2.0);
    for (const [x, z] of [
      [-18.5, lot.z + 7.2],
      [-17.6, lot.z + 7.4],
    ] as const) {
      const f = flamingo();
      this.place(f, x, z, rng.range(0, 6));
    }
    const fence = picketFence(6);
    this.place(fence, -22, lot.z + 6.2);
    this.collision.addRect(-22, lot.z + 6.2, 6, 0.2);
    const blessed = signBoard(
      {
        w: 512,
        h: 160,
        bg: '#f7efe3',
        fg: '#6b2346',
        lines: [{ text: 'Blessed', size: 100, font: 'Pacifico' }],
      },
      1.4,
      0.45,
      0.6,
    );
    this.place(blessed, -15, lot.z + 4.8);
    for (let i = 0; i < 6; i++) {
      const fl = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 6, 4),
        mat(rng.pick(['#e63946', '#ffd166', '#f78fb3', '#c77dff'])),
      );
      fl.position.set(-24.5 + i * 0.9, 0.25, lot.z + 5.6);
      this.root.add(fl);
    }
  }

  private decorateDale(rng: Random): void {
    const lot = LOTS.dale;
    const w = washer();
    this.place(w, 21, lot.z + 1.6, 0.3);
    this.collision.addRect(21, lot.z + 1.6, 0.8, 0.8);
    const mat2 = mattress();
    mat2.rotation.z = 1.2;
    this.place(mat2, 23.8, lot.z + 0.6, 0.2);
    this.collision.addRect(23.8, lot.z + 0.6, 1.0, 1.6);
    const c = couch('#6b4a2a');
    this.place(c, 17.5, lot.z + 4.2, Math.PI + 0.2);
    this.collision.addRect(17.5, lot.z + 4.2, 2.1, 1.0);
    this.place(tireStack(rng, 5), 26.3, lot.z + 4.8);
    this.collision.addCircle(26.3, lot.z + 4.8, 0.5);
    this.place(trashBags(rng, 4), 24.5, lot.z + 3.0);
    const car = buildCar({ kind: 'sedan', color: '#6a6f2e', onBlocks: true, rusty: true });
    this.place(car.group, 7.8, lot.z + 3.4, Math.PI);
    this.collision.addRect(7.8, lot.z + 3.4, 4.8, 2.0);
  }

  private decorateJenkins(): void {
    const lot = LOTS.jenkins;
    const chair = rockingChair();
    this.place(chair, SPOTS.jenkinsChair.x, SPOTS.jenkinsChair.z - 0.05, 0, this.dynamic);
    const sign = signBoard(
      {
        w: 512,
        h: 230,
        bg: '#f4d03f',
        fg: '#111',
        border: '#111',
        borderWidth: 10,
        lines: [
          { text: 'BEWARE OF', size: 60, font: 'Anton' },
          { text: 'OWNER', size: 90, font: 'Anton', color: '#b31d1d' },
        ],
        grime: true,
      },
      1.0,
      0.45,
      0.7,
      1,
    );
    this.place(sign, -6.2, lot.z + 1.5, Math.PI / 2 - 0.3);
    const flag = flagpole(2.6, texMat(americanFlagTexture(), { side: THREE.DoubleSide }), 0.9, 0.5);
    this.place(flag.group, -10.9, lot.z - 0.3);
    this.animators.push((t) => waveFlag(flag.flag, t + 2, 0.7));
    const truck = buildCar({ kind: 'pickup', color: '#a5462a', rusty: true });
    this.place(truck.group, -7.6, lot.z + 3.6, 0);
    this.collision.addRect(-7.6, lot.z + 3.6, 5.4, 2.1);
  }

  private decorateSkeeter(): void {
    const lot = LOTS.skeeter;
    this.place(bmxRamp(), SPOTS.skeeterRamp.x - 1, SPOTS.skeeterRamp.z + 0.5, Math.PI / 2);
    this.collision.addRect(SPOTS.skeeterRamp.x - 1, SPOTS.skeeterRamp.z + 0.5, 1.3, 2.1);
    const b = bike('#e33');
    b.rotation.z = Math.PI / 2;
    b.position.y = 0.05;
    this.place(b, -14.5, lot.z + 3.5, 0.6);
    this.place(basketballHoop(), -4.6, lot.z + 5, Math.PI / 2);
    this.collision.addCircle(-4.6, lot.z + 5, 0.2);
  }

  private decorateBrenda(): void {
    const lot = LOTS.brenda;
    const boat = buildBoatOnTrailer();
    this.place(boat, 21, lot.z + 4.5, 0.1);
    this.collision.addRect(21, lot.z + 4.5, 5.2, 1.9);
    this.place(lawnChair('#e0b000'), 14, lot.z + 3.8, Math.PI);
  }

  private decorateWanda(): void {
    const lot = LOTS.wanda;
    const hats = ['#c0392b', '#2b6cb0', '#2f8f3a', '#e0b000', '#8e44ad'];
    for (let i = 0; i < 14; i++) {
      const g = gnome(hats[i % hats.length] as string);
      this.place(g, -24 + (i % 7) * 1.6, lot.z + 3 + Math.floor(i / 7) * 1.4, (i * 0.7) % 6);
    }
    const sign = signBoard(
      {
        w: 512,
        h: 160,
        bg: '#fff8e1',
        fg: '#2f6b3a',
        lines: [{ text: 'Gnome Sweet Gnome', size: 70, font: 'Pacifico' }],
      },
      1.5,
      0.45,
      0.6,
    );
    this.place(sign, -14, lot.z + 5.5);
  }

  private decorateHank(): void {
    const lot = LOTS.hank;
    const pool = new THREE.Group();
    pool.add(cyl(2.2, 2.2, 1.1, mat('#3d86c6', { side: THREE.DoubleSide }), 0, 0.55, 0, 24, { open: true }));
    const water = mesh(new THREE.CircleGeometry(2.15, 24), mat('#4fb3d9', { rough: 0.1 }), 0, 0.95, 0, {
      cast: false,
    });
    water.rotation.x = -Math.PI / 2;
    pool.add(water);
    this.place(pool, 21.5, lot.z + 3.5);
    this.collision.addCircle(21.5, lot.z + 3.5, 2.3);
    const mower = new THREE.Group();
    mower.add(box(1.2, 0.5, 0.9, mat('#2f7d32'), 0, 0.5, 0));
    mower.add(box(0.5, 0.4, 0.5, M.black, -0.2, 0.9, 0));
    for (const x of [-0.45, 0.45])
      for (const z of [-0.45, 0.45]) {
        const w = cyl(0.22, 0.22, 0.15, M.rubber, x, 0.22, z, 10);
        w.rotation.x = Math.PI / 2;
        mower.add(w);
      }
    this.place(mower, 13.5, lot.z + 4.2, 0.4);
    this.collision.addRect(13.5, lot.z + 4.2, 1.3, 1.0);
  }

  private buildPlayerCar(): void {
    const c = homeSpots(LOT_7).camaro;
    this.rideRoot.position.set(c.x, 0, c.z);
    this.dynamic.add(this.rideRoot);
    // The ride's colliders are managed by the Game, since it can drive off.
    this.setRide('camaro', true);
  }

  /** Puts a vehicle in your driveway: the Camaro (on blocks until she runs) or anything from Cooter's. */
  setRide(id: RideId, onBlocks = false): void {
    const key = `${id}|${id === 'camaro' && onBlocks}`;
    if (this.ride && this.rideKey === key) return;
    this.rideKey = key;
    if (this.ride) {
      this.rideRoot.remove(this.ride.group);
      this.ride.group.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose();
      });
    }
    this.ride = buildRide(id, { onBlocks });
    this.ride.group.traverse((o) => (o.userData.noBatch = true));
    this.rideRoot.add(this.ride.group);
    if (this.truckNutz) this.ride.rear.add(this.truckNutz);
  }

  /** Mud under a point (the bog), for driving and walking. */
  mudAt(x: number, z: number): number {
    return mudAt(x, z);
  }

  setTruckNutz(on: boolean): void {
    if (on && !this.truckNutz) {
      this.truckNutz = buildTruckNutz();
      this.ride.rear.add(this.truckNutz);
    }
  }

  // ---------------------------------------------------------------------------
  // Entrance, stores, junkyard
  // ---------------------------------------------------------------------------

  private buildEntrance(rng: Random): void {
    const sign = new THREE.Group();
    const classic = signTexture({
      w: 1024,
      h: 512,
      bg: '#f3e6c4',
      fg: '#3a2412',
      border: '#3a2412',
      borderWidth: 18,
      gap: 10,
      grime: true,
      lines: [
        { text: 'LUCKY ACRES', size: 150, font: 'Rye', color: '#2f6b3a' },
        { text: 'Mobile Home Park', size: 70, font: 'Rye' },
        { text: 'EST. 1974 • LOT RENT DUE FRIDAYS', size: 46, font: 'Anton', color: '#7a1f2b' },
      ],
    });
    const apex = signTexture({
      w: 1024,
      h: 512,
      bg: '#f3e6c4',
      fg: '#3a2412',
      border: '#3a2412',
      borderWidth: 18,
      gap: 10,
      grime: true,
      lines: [
        { text: 'LUCKY ACRES', size: 150, font: 'Rye', color: '#2f6b3a' },
        { text: 'Mobile Home Park', size: 70, font: 'Rye' },
        { text: 'An APEX CAPITAL Community™', size: 54, font: 'Anton', color: '#1d2b3a' },
      ],
    });
    this.parkSignClassic = mesh(new THREE.PlaneGeometry(4.4, 2.2), texMat(classic), 0, 2.4, 0.08, {
      cast: false,
    });
    this.parkSignApex = mesh(new THREE.PlaneGeometry(4.4, 2.2), texMat(apex), 0, 2.4, 0.08, { cast: false });
    const owned = signTexture({
      w: 1024,
      h: 512,
      bg: '#f3e6c4',
      fg: '#3a2412',
      border: '#3a2412',
      borderWidth: 18,
      gap: 10,
      grime: true,
      lines: [
        { text: 'LUCKY ACRES', size: 150, font: 'Rye', color: '#2f6b3a' },
        { text: 'Mobile Home Park', size: 70, font: 'Rye' },
        { text: 'RESIDENT OWNED • NO BRENTS ALLOWED', size: 50, font: 'Anton', color: '#b8860b' },
      ],
    });
    this.parkSignOwned = mesh(new THREE.PlaneGeometry(4.4, 2.2), texMat(owned), 0, 2.4, 0.08, {
      cast: false,
    });
    this.parkSignApex.visible = false;
    this.parkSignOwned.visible = false;
    this.parkSignClassic.userData.noBatch = true;
    this.parkSignApex.userData.noBatch = true;
    this.parkSignOwned.userData.noBatch = true;
    sign.add(this.parkSignClassic, this.parkSignApex, this.parkSignOwned);
    sign.add(box(4.6, 2.4, 0.15, M.pole, 0, 2.4, 0));
    for (const x of [-2, 2]) sign.add(box(0.25, 3.6, 0.25, M.pole, x, 1.8, -0.1));
    sign.add(box(5.2, 0.6, 0.8, mat('#7b6f62'), 0, 0.3, 0));
    for (let i = 0; i < 5; i++) {
      const fl = new THREE.Mesh(
        new THREE.SphereGeometry(0.15, 6, 4),
        mat(rng.pick(['#e63946', '#ffd166', '#f78fb3'])),
      );
      fl.position.set(-2 + i, 0.7, 0.35);
      sign.add(fl);
    }
    this.place(sign, SPOTS.parkSign.x, SPOTS.parkSign.z);
    this.collision.addRect(SPOTS.parkSign.x, SPOTS.parkSign.z, 5.2, 0.9);

    const small = (text: string[], x: number, z: number, bg = '#ffd400', fg = '#111'): void => {
      const s = signBoard(
        {
          w: 384,
          h: 384,
          bg,
          fg,
          border: fg,
          borderWidth: 12,
          gap: 4,
          lines: text.map((t, i) => ({ text: t, size: i === 0 ? 72 : 46, font: 'Anton' })),
          grime: true,
        },
        0.9,
        0.9,
        1.4,
        1,
      );
      this.place(s, x, z);
      this.collision.addCircle(x, z - 0.1, 0.15);
    };
    small(['SLOW', 'KIDS & DOGS', '(MOSTLY DOGS)'], 4.9, 50);
    small(['SPEED', 'LIMIT', '5'], 4.9, 22, '#ffffff');
    small(['NO', 'FIREWORKS', '(SERIOUSLY)'], -4.9, 34, '#ffffff', '#b31d1d');
    small(['NO LOT 13', "DON'T ASK"], -4.9, -29.5, '#ffffff');

    const office = parkOffice();
    this.place(office, SPOTS.officeCenter.x, SPOTS.officeCenter.z, Math.PI / 2);
    this.solid(SPOTS.officeCenter.x, SPOTS.officeCenter.z, 8, 3.5, 3.6, 1);
    this.collision.addRect(-11.1, 46, 2.4, 3.5);
    this.place(lawnChair('#c2185b'), -8.6, 43.6, 1.2);

    const dump = dumpster();
    this.place(dump, SPOTS.dumpster.x, SPOTS.dumpster.z - 1.0);
    this.solid(SPOTS.dumpster.x, SPOTS.dumpster.z - 1.0, 2.5, 1.6, 1.5);
    this.place(trashBags(rng, 3), SPOTS.dumpster.x + 1.8, SPOTS.dumpster.z - 0.6);

    // Big Dave's Mobile Homes.
    const dw = doubleWide();
    this.place(dw, SPOTS.bigDaveHome.x, SPOTS.bigDaveHome.z);
    this.solid(SPOTS.bigDaveHome.x, SPOTS.bigDaveHome.z, 16, 8, 5);
    this.collision.addRect(SPOTS.bigDaveHome.x, SPOTS.bigDaveHome.z + 5.1, 4.4, 2.2);
    const daveSign = signBoard(
      {
        w: 1024,
        h: 512,
        bg: '#ffffff',
        fg: '#1d4e9e',
        border: '#c62828',
        borderWidth: 22,
        gap: 6,
        lines: [
          { text: "BIG DAVE'S HOMES", size: 120, font: 'Bangers', color: '#c62828' },
          { text: 'DOUBLE WIDES • $2,500 DOWN*', size: 70, font: 'Anton' },
          { text: 'NO CREDIT? NO PROBLEM!', size: 70, font: 'Anton', color: '#111' },
          { text: '*plus tax, title, fees & your firstborn', size: 34, font: 'Anton', color: '#666' },
        ],
      },
      5,
      2.5,
      2.2,
    );
    this.place(daveSign, 35, 53);
    this.collision.addRect(35, 52.9, 5, 0.4);
    const tm = tubeMan();
    this.place(tm.group, SPOTS.tubeMan.x, SPOTS.tubeMan.z, 0, this.dynamic);
    this.collision.addCircle(SPOTS.tubeMan.x, SPOTS.tubeMan.z, 0.5);
    this.animators.push((t) => waveTubeMan(tm, t));
    const cols = ['#c62828', '#ffffff', '#1d4e9e', '#ffd400'];
    const p1 = new THREE.Vector3(9.5, 4, 40.5);
    const p2 = new THREE.Vector3(38.5, 4, 40.5);
    const p3 = new THREE.Vector3(38.5, 4, 54.5);
    const p4 = new THREE.Vector3(9.5, 4, 54.5);
    for (const p of [p1, p2, p3, p4]) {
      this.root.add(cyl(0.06, 0.06, 4, M.steel, p.x, 2, p.z, 6));
      this.collision.addCircle(p.x, p.z, 0.12);
    }
    // No string along the front: it would hang across Big Dave's billboard.
    this.root.add(pennants(p1, p4, cols), pennants(p2, p3, cols));
  }

  private buildStores(rng: Random): void {
    const gas = gasStation();
    this.place(gas, -30, 87, Math.PI);
    this.solid(-30, 87, 16, 10, 4.6);
    // Ice chest and propane cage (the building is rotated 180°, so local x flips).
    this.collision.addRect(-34.1, 81.2, 1.5, 0.9);
    this.collision.addRect(-23.4, 81.4, 1.3, 0.9);
    const canopy = gasCanopy();
    this.place(canopy, SPOTS.gasCanopy.x, SPOTS.gasCanopy.z);
    for (const x of [-6, 6])
      for (const z of [-3.5, 3.5])
        this.collision.addRect(SPOTS.gasCanopy.x + x, SPOTS.gasCanopy.z + z, 0.5, 0.5);
    for (const x of [-3, 3]) {
      this.place(gasPump(), SPOTS.gasCanopy.x + x, SPOTS.gasCanopy.z, Math.PI / 2);
      this.collision.addRect(SPOTS.gasCanopy.x + x, SPOTS.gasCanopy.z, 0.8, 1.0);
    }
    this.canopyLight.position.set(SPOTS.gasCanopy.x, 4.2, SPOTS.gasCanopy.z);
    const ps = priceSign();
    this.place(ps, -43, 69.5, Math.PI);
    this.collision.addCircle(-43, 69.5, 0.3);
    this.place(payphone(), SPOTS.payphone.x, SPOTS.payphone.z + 0.4, Math.PI);
    this.collision.addCircle(SPOTS.payphone.x, SPOTS.payphone.z + 0.45, 0.25);

    const laundry = laundromat();
    this.place(laundry, 0, 87, Math.PI);
    this.solid(0, 87, 16, 10, 4.4);
    this.place(bench(), 4.5, 81.2, Math.PI);
    this.collision.addRect(4.5, 81.2, 1.6, 0.5);

    const pawn = pawnShop();
    this.place(pawn, 23, 87, Math.PI);
    this.solid(23, 87, 14, 10, 4.6);

    const parked = buildCar({ kind: 'sedan', color: rng.pick(['#d9d4c7', '#3a3f45', '#7a1f2b']) });
    this.place(parked.group, 12, 73, Math.PI / 2);
    this.collision.addRect(12, 73, 2.0, 4.8);
    const parked2 = buildCar({ kind: 'pickup', color: '#6b7a3a', rusty: true });
    this.place(parked2.group, -12, 74, -Math.PI / 2);
    this.collision.addRect(-12, 74, 2.1, 5.4);
  }

  private buildJunkyard(rng: Random): void {
    const j = JUNKYARD;
    const fence = (x1: number, z1: number, x2: number, z2: number): void => {
      const len = Math.hypot(x2 - x1, z2 - z1);
      const f = chainLinkFence(len, 2.4);
      this.place(f, (x1 + x2) / 2, (z1 + z2) / 2, -Math.atan2(z2 - z1, x2 - x1));
      this.collision.addRect((x1 + x2) / 2, (z1 + z2) / 2, Math.abs(x2 - x1) + 0.2, Math.abs(z2 - z1) + 0.2);
    };
    fence(j.minX, j.minZ, j.maxX, j.minZ);
    fence(j.maxX, j.minZ, j.maxX, j.maxZ);
    fence(j.minX, j.maxZ, j.maxX, j.maxZ);
    fence(j.minX, j.minZ, j.minX, -51.5);
    fence(j.minX, -44.5, j.minX, j.maxZ);

    const shack = junkyardShack();
    this.place(shack, SPOTS.junkyardShack.x, SPOTS.junkyardShack.z);
    this.solid(SPOTS.junkyardShack.x, SPOTS.junkyardShack.z, 5, 4, 3.3);

    const colors = ['#7a1f2b', '#2e4f8a', '#6b7a3a', '#d9d4c7', '#3a3f45', '#a5462a', '#c9a23a'];
    const stacks: Array<[number, number]> = [
      [55, -39],
      [63, -41],
      [67, -52],
      [57, -58],
      [50, -38.5],
    ];
    for (const [x, z] of stacks) {
      const n = rng.int(2, 4);
      for (let i = 0; i < n; i++) {
        const car = crushedCar(rng.pick(colors), rng);
        car.position.y = i * 0.95;
        this.place(car, x + rng.range(-0.3, 0.3), z + rng.range(-0.3, 0.3), rng.range(-0.2, 0.2));
        car.position.y = i * 0.95;
      }
      this.collision.addRect(x, z, 4.2, 2.2);
    }
    for (const [x, z] of [
      [66, -38],
      [49, -60],
      [61, -59],
    ] as const) {
      for (let i = 0; i < 3; i++)
        this.place(tireStack(rng, rng.int(2, 5)), x + (i % 2) * 0.9, z + Math.floor(i / 2) * 0.9);
      this.collision.addCircle(x + 0.45, z + 0.3, 1.1);
    }
    for (let i = 0; i < 6; i++) {
      const f = fridge(rng.pick(['#f0ede4', '#d8c9a3', '#9aa39a']));
      const x = rng.range(52, 68);
      const z = rng.range(-50, -44);
      this.place(f, x, z, rng.range(0, 6));
      this.collision.addCircle(x, z, 0.5);
    }
    const drop = signTexture({
      w: 512,
      h: 256,
      bg: '#f4d03f',
      fg: '#111',
      lines: [{ text: 'DROP-OFF', size: 110, font: 'Anton' }],
    });
    // The gravel under it is pulled toward the camera (see surface()); pull harder so the
    // sign doesn't sink into it at low angles.
    const padMat = texMat(drop, { transparent: false }).clone();
    padMat.polygonOffset = true;
    padMat.polygonOffsetFactor = -4;
    padMat.polygonOffsetUnits = -4;
    const pad = mesh(
      new THREE.PlaneGeometry(4, 2),
      padMat,
      SPOTS.junkyardDrop.x,
      0.04,
      SPOTS.junkyardDrop.z,
      { cast: false },
    );
    pad.rotation.x = -Math.PI / 2;
    this.root.add(pad);
  }

  // ---------------------------------------------------------------------------
  // Utilities & nature
  // ---------------------------------------------------------------------------

  private buildUtilities(): void {
    const poleZ = [53, 37, 21, 5, -11, -27, -43];
    const anchors: THREE.Vector3[][] = [];
    poleZ.forEach((z, i) => {
      // Crossarm runs across the street (x); wires run along it (z).
      const p = powerPole(i % 3 === 1);
      this.place(p.group, 4.8, z);
      this.collision.addCircle(4.8, z, 0.25);
      anchors.push(p.anchors.map((a) => new THREE.Vector3(4.8 + a.x, a.y, z + a.z)));
    });
    for (let i = 0; i < anchors.length - 1; i++)
      for (let k = 0; k < 2; k++) this.root.add(wire(anchors[i]![k]!, anchors[i + 1]![k]!, 0.9));

    const hwAnchors: THREE.Vector3[][] = [];
    // Offset so no pole lands in the park entrance at x = 0.
    for (let x = -96.25; x <= 110; x += 27.5) {
      const p = powerPole(false);
      this.place(p.group, x, 54.2, Math.PI / 2);
      this.collision.addCircle(x, 54.2, 0.25);
      hwAnchors.push(p.anchors.map((a) => new THREE.Vector3(x + a.z, a.y, 54.2 - a.x)));
    }
    for (let i = 0; i < hwAnchors.length - 1; i++)
      for (let k = 0; k < 2; k++) this.root.add(wire(hwAnchors[i]![k]!, hwAnchors[i + 1]![k]!, 1.1));

    for (const z of [40, 8, -24]) {
      const sl = streetLight();
      this.place(sl.group, -4.9, z, Math.PI / 2);
      this.collision.addCircle(-4.9, z, 0.2);
      const light = new THREE.PointLight('#ffcf80', 0, 20, 1.5);
      light.position.set(-4.9 + sl.lamp.z, sl.lamp.y - 0.2, z);
      this.scene.add(light);
      this.streetLights.push(light);
    }

    // BMX field: ramps, a curbside couch and a burn barrel.
    const f = SPOTS.bmxField;
    this.place(bmxRamp(), f.x - 4, f.z, 0.3);
    this.place(bmxRamp(), f.x + 4, f.z + 2, Math.PI - 0.2);
    this.collision.addRect(f.x - 4, f.z, 2.2, 1.4);
    this.collision.addRect(f.x + 4, f.z + 2, 2.2, 1.4);
    // The curb couch is its own object: some nights it gets set on fire.
    const c = couch('#4a6b8a');
    c.traverse((o) => (o.userData.noBatch = true));
    this.place(c, f.x, f.z - 6, 0.1, this.dynamic);
    this.curbCouch = c;
    this.collision.addRect(f.x, f.z - 6, 2.1, 1);
    const barrel = cyl(0.32, 0.32, 0.9, mat('#5a3a28', { metal: 0.3 }), 0, 0.45, 0, 10);
    const barrelG = new THREE.Group();
    barrelG.add(barrel);
    this.place(barrelG, f.x + 2.2, f.z - 5.2);
    this.collision.addCircle(f.x + 2.2, f.z - 5.2, 0.4);
    this.fireBarrelLight.position.set(f.x + 2.2, 1.4, f.z - 5.2);
    this.smokeSources.push(new THREE.Vector3(f.x + 2.2, 1.0, f.z - 5.2));
  }

  private isClear(x: number, z: number): boolean {
    const blocked: Rect[] = [
      rect(-34, -56, 34, 58),
      rect(-140, 52, 140, 70),
      rect(-50, 64, 42, 100),
      rect(34, -66, 76, -30),
      rect(-30, -72, 8, -50),
      rect(6, 38, 42, 58),
      // Possum Trot Lane, the Commons and the Back Forty (which gets dead trees instead).
      rect(-96, -56, -33, 56),
      rect(BACK_FORTY.minX - 4, BACK_FORTY.minZ - 4, BACK_FORTY.maxX + 3, BACK_FORTY.maxZ + 3),
      rect(-120, CRICK.z - 6, -18, CRICK.z + 6),
      // The south strip, Cooter's lot, the fairgrounds and the gun range.
      rect(-112, 64, -44, 96),
      rect(38, 64, 90, 96),
      rect(50, -44, 66, 58),
      rect(RODEO.x - RODEO.rx - 8, RODEO.z - RODEO.rz - 6, RODEO.x + RODEO.rx + 6, RODEO.z + RODEO.rz + 12),
      rect(MUD_BOG.minX - 6, MUD_BOG.minZ - 6, MUD_BOG.maxX + 5, MUD_BOG.maxZ + 5),
      rect(GUN_RANGE.minX - 4, GUN_RANGE.minZ - 4, GUN_RANGE.maxX + 4, GUN_RANGE.maxZ + 4),
    ];
    for (const r of blocked) if (inRect(r, x, z)) return false;
    return true;
  }

  private buildNature(rng: Random): void {
    let placed = 0;
    for (let i = 0; i < 1400 && placed < 300; i++) {
      const x = rng.range(-112, 112);
      const z = rng.range(-90, 116);
      if (!this.isClear(x, z)) continue;
      const edge = Math.max(Math.abs(x) / 112, z < 0 ? Math.abs(z) / 90 : 0, z > 100 ? 1 : 0);
      if (rng.next() > lerp(0.25, 1, edge)) continue;
      const tree = rng.chance(0.68) ? pineTree(rng) : leafyTree(rng);
      this.place(tree, x, z);
      this.collision.addCircle(x, z, 0.5);
      placed++;
    }
    for (let i = 0; i < 160; i++) {
      const x = rng.range(-100, 100);
      const z = rng.range(-80, 110);
      if (!this.isClear(x, z)) continue;
      this.place(bush(rng), x, z);
    }
    // A few shade trees inside the park.
    for (const [x, z] of [
      [-29, 22],
      [29, 6],
      [-29, -10],
      [29, -26],
      [-30, 40],
    ] as const) {
      this.place(leafyTree(rng), x, z);
      this.collision.addCircle(x, z, 0.5);
    }
  }

  // ---------------------------------------------------------------------------
  // Runtime
  // ---------------------------------------------------------------------------

  /** The entrance sign: the original, the Apex rebrand, or resident-owned. */
  setParkSign(state: 'classic' | 'apex' | 'owned'): void {
    this.parkSignClassic.visible = state === 'classic';
    this.parkSignApex.visible = state === 'apex';
    this.parkSignOwned.visible = state === 'owned';
  }

  update(time: number, dt: number, night: number): void {
    for (const a of this.animators) a(time, dt, night);
    const lampOn = smoothstep(0.35, 0.7, night);
    for (const l of this.streetLights) l.intensity = lampOn * 28;
    this.porchLight.intensity = lampOn * 10;
    this.canopyLight.intensity = lampOn * 50;
    this.fireBarrelLight.intensity =
      lampOn * (9 + Math.sin(time * 13) * 2 + Math.sin(time * 7.7) * 2) +
      this.fireBoost * (1 + Math.sin(time * 17) * 0.2);
    this.campLight.intensity = (0.25 + lampOn) * (14 + Math.sin(time * 11) * 3 + Math.sin(time * 6.1) * 2);

    this.smokeTimer -= dt;
    if (this.smokeTimer <= 0) {
      this.smokeTimer = 0.18;
      for (const s of this.smokeSources) {
        this.smoke.spawn({
          x: s.x + (Math.random() - 0.5) * 0.2,
          y: s.y,
          z: s.z + (Math.random() - 0.5) * 0.2,
          vx: 0.3 + Math.random() * 0.2,
          vy: 0.9 + Math.random() * 0.4,
          vz: (Math.random() - 0.5) * 0.3,
          life: 3.2,
          size: 0.5,
          grow: 3.2,
          color: '#9a9a96',
          drag: 0.2,
        });
      }
      if (lampOn > 0.2) {
        const s = this.smokeSources[this.smokeSources.length - 1]!;
        this.sparks.spawn({
          x: s.x,
          y: s.y + 0.1,
          z: s.z,
          vx: (Math.random() - 0.5) * 0.6,
          vy: 1.6 + Math.random(),
          vz: (Math.random() - 0.5) * 0.6,
          life: 0.9,
          size: 0.12,
          color: '#ffae42',
          drag: 0.4,
        });
      }
    }
    this.sparks.update(dt);
    this.smoke.update(dt);
  }

  setParticleScale(viewportHeight: number): void {
    this.sparks.setPixelScale(viewportHeight);
    this.smoke.setPixelScale(viewportHeight);
  }
}

function waveTubeMan(tm: ReturnType<typeof tubeMan>, t: number): void {
  tm.segments.forEach((s, i) => {
    s.rotation.z = Math.sin(t * 3.1 + i * 0.9) * 0.28 + Math.sin(t * 7.3 + i) * 0.08;
    s.rotation.x = Math.sin(t * 2.3 + i * 1.3) * 0.15;
  });
  tm.arms.forEach((a, i) => {
    a.rotation.z = (i === 0 ? 1 : -1) * (1.2 + Math.sin(t * 6 + i * 2) * 0.9);
  });
}
